import { useQuery } from "@apollo/client";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import {
  ComplianceProgramActivityRowFragmentFragment,
  ComplianceProgramEventFragmentFragment,
  GetComplianceProgramActivityQuery,
  GetComplianceProgramActivityQueryVariables,
  GetComplianceProgramEventsQuery,
  GetComplianceProgramEventsQueryVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import { Select, SelectOption } from "@components/select/select";
import Table, {
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableContainer,
  TableHeadCell,
  TableHeadRow,
} from "@components/table/table";
import { TableSticky } from "@components/table/table-constants";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { assertEdgesNonNull } from "@helpers/helpers";

import getComplianceProgramActivityQuery from "../graphql/get-compliance-program-activity-query";
import getComplianceProgramEventsQuery from "../graphql/get-compliance-program-events-query";
import { getRecurredDates, getSkipReason } from "../helpers";
import { useProgramsSection } from "../programs-section-context";
import {
  formatReminderSchedule,
  formatScheduleValue,
} from "./compliance-program-activity-helpers";

/**
 * Nothing that happened before the activity log shipped was recorded. On a cycle created
 * before this date we say so, rather than showing an empty table that reads as "nothing
 * happened". Set this to the deploy date.
 */
export const ACTIVITY_HISTORY_STARTS_ON = "2026-09-08";

/**
 * Every verb the server can write, in the words the product uses. The verbs are named
 * after reviews, but this tab is also mounted under Surveys and Programs, so the noun
 * comes from the section rather than from the review label.
 */
const useEventLabels = (cycle: string) => {
  return useMemo<Record<string, string>>(
    () => ({
      "review.published": `${cycle} published`,
      "review.kickoff": `${cycle} kickoff`,
      "review.paused": `${cycle} paused`,
      "review.resumed": `${cycle} resumed`,
      "review.closed": `${cycle} closed`,
      "review.reopened": `${cycle} reopened`,
      "review.dates_changed": `${cycle} dates changed`,
      "review.recurred": `Next ${cycle.toLowerCase()} created`,
      "roster.synced": "Participants synced",
      "roster.participant_added": "Participant added",
      "roster.participant_removed": "Participant removed",
      "roster.participant_joined": `Joined the ${cycle.toLowerCase()}`,
      "notification.sent": "Notification sent",
      "reminder.created": "Reminder created",
      "reminder.updated": "Reminder updated",
      "reminder.deleted": "Reminder deleted",
      "reminder.sent": "Reminder sent",
      "assessment.reopened": "Assessment reopened",
    }),
    [cycle],
  );
};

const sourceLabels: Record<string, string> = {
  web: "web",
  slack: "Slack",
  teams: "Teams",
  sms: "SMS",
  mcp: "MCP client",
  cli: "command line",
  cron: "schedule",
  signal: "system",
  routine: "routine",
};

const channelLabels: Record<string, string> = {
  slack: "Slack",
  teams: "Teams",
  email: "email",
};

const statusLabels: Record<string, string> = {
  sent: "sent",
  failed: "failed",
  skipped: "skipped",
};

/**
 * The dates a review event can report a change to. The evaluation period belongs to the
 * cycle, so it takes the section's noun the way the verb labels above do.
 */
const useDateFieldLabels = (cycle: string) =>
  useMemo<Record<string, string>>(
    () => ({
      start_date: "Kickoff date",
      due_date: "Due date",
      period_start_date: `${cycle} period start`,
      period_end_date: `${cycle} period end`,
      start_date_offset: "Kickoff",
      start_date_due_date_offset: "Due date",
      start_date_period_start_offset: `${cycle} period start`,
      start_date_period_end_offset: `${cycle} period end`,
    }),
    [cycle],
  );

/** A step's name, in the same words the reminders panel gives it. */
const useStepLabels = () => {
  const label = useLabel();
  return useMemo<Record<string, string>>(
    () => ({
      pre_calibration: `Pre-${label("review")} calibration`,
      peer_nomination: "Peer nomination",
      self_review: `Self ${label("review", { capitalize: true })}`,
      peer_review: `Peer ${label("review", { capitalize: true })}`,
      upward_review: `Upward ${label("review", { capitalize: true })}`,
      downward_review: `Manager ${label("review", { capitalize: true })}`,
      post_calibration: `Post-${label("review")} calibration`,
      delivery: "Delivery",
      one_on_one: label("oneonone", { capitalize: true }),
      engagement_survey: "Survey",
    }),
    [label],
  );
};

const reminderChangeVerbs = new Set([
  "reminder.created",
  "reminder.updated",
  "reminder.deleted",
]);

/**
 * What the server put in an admin action's payload. Every field is optional: a payload
 * belongs to one verb, and an older row can predate a field this tab now reads. A skip
 * reason is read by `getSkipReason` instead, because notification rows carry it too.
 */
type EventPayload = {
  changes?: {
    field: string;
    from: string | number | null;
    to: string | number | null;
  }[];
  added?: number;
  removed?: number;
  role_changed?: number;
  steps?: string[];
  channels?: string[];
  scheduled_date?: string | null;
  days_after_start_date?: number;
  writer_name?: string | null;
};

/**
 * The payload arrives as a JSON string, because the server sends a JSONString scalar.
 *
 * A row we cannot read is still a row that happened, so anything unexpected becomes an
 * empty payload and the row shows without its detail. `JSON.parse` also accepts `null`
 * and arrays, neither of which is a payload.
 */
const parsePayload = (raw: unknown): EventPayload => {
  const parsed = typeof raw === "string" ? tryParseJson(raw) : raw;
  return parsed && typeof parsed === "object" && !Array.isArray(parsed)
    ? (parsed as EventPayload)
    : {};
};

const tryParseJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** A payload list, or an empty one when the stored value is not a list after all. */
const asList = <T,>(value: T[] | undefined): T[] =>
  Array.isArray(value) ? value : [];

/**
 * Turns an admin action's payload into the parts of its Details column that do not come
 * from a column of their own: which dates moved, how many people the sync touched, which
 * steps and channels a reminder covers, who wrote a reopened assessment.
 */
const usePayloadDetails = (cycle: string) => {
  const dateFieldLabels = useDateFieldLabels(cycle);
  const stepLabels = useStepLabels();

  return useCallback(
    (verb: string, rawPayload: unknown): string[] => {
      const payload = parsePayload(rawPayload);
      if (verb === "review.dates_changed") {
        return asList(payload.changes).map(
          (change) =>
            `${dateFieldLabels[change.field] ?? change.field} changed from ` +
            `${formatScheduleValue(change.field, change.from)} to ` +
            `${formatScheduleValue(change.field, change.to)}`,
        );
      }
      if (verb === "roster.synced") {
        return [
          `${payload.added ?? 0} added`,
          `${payload.removed ?? 0} removed`,
          `${payload.role_changed ?? 0} with a new role`,
        ];
      }
      if (reminderChangeVerbs.has(verb)) {
        const steps = asList(payload.steps).map(
          (step) => stepLabels[step] ?? step,
        );
        return [
          steps.join(", "),
          asList(payload.channels)
            .map((channel) => channelLabels[channel] ?? channel)
            .join(", "),
          formatReminderSchedule(
            payload.scheduled_date,
            payload.days_after_start_date,
          ),
        ].filter(Boolean);
      }
      if (verb === "assessment.reopened" && payload.writer_name) {
        return [`written by ${payload.writer_name}`];
      }
      if (verb === "review.recurred") {
        // A new cycle appearing on its own is only useful news if it says when it runs.
        return [getRecurredDates(rawPayload)].filter(
          (part): part is string => !!part,
        );
      }
      return [];
    },
    [dateFieldLabels, stepLabels],
  );
};

enum ActivityFilter {
  All = "All",
  Actions = "Actions",
  Notifications = "Notifications",
  Reminders = "Reminders",
}

/**
 * "Actions" is everything a person did to the cycle itself, so it is expressed as the
 * verb prefixes that are not a notification or a reminder.
 */
const verbPrefixesByFilter: Record<ActivityFilter, string[] | null> = {
  [ActivityFilter.All]: null,
  // eslint-disable-next-line local-rules/use-label -- these are server verb prefixes, not text a person reads
  [ActivityFilter.Actions]: ["review.", "roster.", "assessment."],
  [ActivityFilter.Notifications]: ["notification."],
  [ActivityFilter.Reminders]: ["reminder."],
};

const activityFilterOptions: SelectOption<ActivityFilter>[] = [
  { value: ActivityFilter.All, label: "All activity" },
  { value: ActivityFilter.Actions, label: "Actions" },
  { value: ActivityFilter.Notifications, label: "Notifications" },
  { value: ActivityFilter.Reminders, label: "Reminders" },
];

/** "138 sent, 3 failed, 4 skipped", leaving out the zeroes. */
const getBatchCounts = (row: ComplianceProgramActivityRowFragmentFragment) =>
  [
    row.sentCount > 0 ? `${row.sentCount} sent` : null,
    row.failedCount > 0 ? `${row.failedCount} failed` : null,
    row.skippedCount > 0 ? `${row.skippedCount} skipped` : null,
  ]
    .filter(Boolean)
    .join(", ");

/**
 * The people behind one send: who it reached, on what channel, and for a skip, why.
 * Fetched only when somebody opens the line, because a send can be 140 rows.
 */
const ComplianceProgramActivityPeopleRows = ({
  complianceProgramId,
  batch,
}: {
  complianceProgramId: number;
  batch: string;
}) => {
  const { data, loading, error, fetchMore } = useQuery<
    GetComplianceProgramEventsQuery,
    GetComplianceProgramEventsQueryVariables
  >(getComplianceProgramEventsQuery, {
    fetchPolicy: "cache-and-network",
    // Oldest first, so a person's Slack failure and the email after it read in that order.
    variables: { complianceProgramId, batch, orderBy: "created" },
  });

  const events: ComplianceProgramEventFragmentFragment[] = useMemo(
    () =>
      data?.complianceProgram?.events
        ? assertEdgesNonNull(data.complianceProgram.events)
        : [],
    [data],
  );
  const pageInfo = data?.complianceProgram?.events.pageInfo;

  // A send to a big roster is more rows than one page holds, and the people left out are
  // the ones an admin came looking for, so the rest have to be reachable.
  const handleLoadMorePeople = () => {
    if (!pageInfo?.endCursor) {
      return;
    }
    fetchMore({ variables: { after: pageInfo.endCursor, merge: true } });
  };

  if (events.length === 0 && loading) {
    return (
      <TableBodyRow>
        <TableBodyCell colSpan={5} className="pl-8">
          <Loading size="5" />
        </TableBodyCell>
      </TableBodyRow>
    );
  }

  if (events.length === 0 && error) {
    return (
      <TableBodyRow>
        <TableBodyCell colSpan={5} className="pl-8 text-gray-500">
          These people could not be loaded. Reload the page to try again.
        </TableBodyCell>
      </TableBodyRow>
    );
  }

  return (
    <>
      {events.map((event) => {
        const detail = [
          event.channel ? channelLabels[event.channel] : null,
          event.status ? statusLabels[event.status] : null,
          getSkipReason(event.payload),
          event.error || null,
        ].filter(Boolean);

        return (
          <TableBodyRow key={event.id}>
            <TableBodyCell className="pl-8 text-gray-500">
              {event.target?.name ?? "Unknown person"}
            </TableBodyCell>
            <TableBodyCell />
            <TableBodyCell className="text-gray-500">
              {moment(event.created).format("MMM D, YYYY h:mm a")}
            </TableBodyCell>
            <TableBodyCell />
            <TableBodyCell className="text-gray-500">
              {detail.join(" · ")}
            </TableBodyCell>
          </TableBodyRow>
        );
      })}
      {pageInfo?.hasNextPage && (
        <TableBodyRow>
          <TableBodyCell colSpan={5} className="pl-8">
            <Button
              theme={buttonTheme.text}
              text="Show more people"
              disabled={loading}
              onClick={handleLoadMorePeople}
            />
          </TableBodyCell>
        </TableBodyRow>
      )}
    </>
  );
};

const ComplianceProgramActivityRow = ({
  complianceProgramId,
  row,
  eventLabels,
  eventDetails,
}: {
  complianceProgramId: number;
  row: ComplianceProgramActivityRowFragmentFragment;
  eventLabels: Record<string, string>;
  eventDetails: (verb: string, payload: unknown) => string[];
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const verbLabel = eventLabels[row.verb] ?? row.verb;
  // A notification line is named after the message, which is what an admin is looking
  // for; the verb alone would say "Notification sent" on nine different things.
  const label = row.notificationLabel ?? verbLabel;
  // A batch is a send, so its line is the counts. Every other line is one event, and an
  // admin action keeps its facts in the payload rather than in a column of its own.
  const detail = row.isBatch
    ? getBatchCounts(row)
    : [
        row.target?.name,
        row.channel ? channelLabels[row.channel] : null,
        row.status ? statusLabels[row.status] : null,
        getSkipReason(row.payload),
        ...eventDetails(row.verb, row.payload),
        row.error || null,
      ]
        .filter(Boolean)
        .join(" · ");

  return (
    <>
      <TableBodyRow>
        <TableBodyCell>
          {row.isBatch ? (
            <button
              type="button"
              className="flex items-center gap-1 text-left"
              aria-expanded={isExpanded}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <TopicflowIcon
                type={isExpanded ? "chevronDown" : "chevronRight"}
                className="h-4 w-4 shrink-0"
              />
              {label}
            </button>
          ) : (
            label
          )}
        </TableBodyCell>
        <TableBodyCell>{row.actor?.name ?? "Topicflow"}</TableBodyCell>
        <TableBodyCell>
          {moment(row.created).format("MMM D, YYYY h:mm a")}
        </TableBodyCell>
        <TableBodyCell>{sourceLabels[row.source] ?? row.source}</TableBodyCell>
        <TableBodyCell>{detail}</TableBodyCell>
      </TableBodyRow>
      {isExpanded && row.isBatch && (
        <ComplianceProgramActivityPeopleRows
          complianceProgramId={complianceProgramId}
          batch={row.batch}
        />
      )}
    </>
  );
};

const ComplianceProgramActivity = ({
  complianceProgramId,
}: {
  complianceProgramId: number;
}) => {
  const { noun } = useProgramsSection();
  const eventLabels = useEventLabels(noun);
  const eventDetails = usePayloadDetails(noun);
  const [selectedFilter, setSelectedFilter] = useState<ActivityFilter>(
    ActivityFilter.All,
  );

  const { data, loading, error, fetchMore } = useQuery<
    GetComplianceProgramActivityQuery,
    GetComplianceProgramActivityQueryVariables
  >(getComplianceProgramActivityQuery, {
    fetchPolicy: "cache-and-network",
    variables: {
      complianceProgramId,
      verbPrefixIn: verbPrefixesByFilter[selectedFilter],
    },
  });

  const rows = useMemo(
    () =>
      data?.complianceProgram?.activity
        ? assertEdgesNonNull(data.complianceProgram.activity)
        : [],
    [data],
  );
  const pageInfo = data?.complianceProgram?.activity.pageInfo;
  const createdDatetime = data?.complianceProgram?.created;
  const startsBeforeTheLogExisted =
    !!createdDatetime &&
    moment(createdDatetime).isBefore(moment(ACTIVITY_HISTORY_STARTS_ON));

  const handleLoadMore = () => {
    if (!pageInfo?.endCursor) {
      return;
    }
    fetchMore({ variables: { after: pageInfo.endCursor, merge: true } });
  };

  return (
    <div className="flex flex-col gap-4" aria-label={`${noun} activity`}>
      {/* A select rather than a second row of tabs, which would sit right under the
          page tabs and read as one confusing stack. Room for more filters later. */}
      <div className="flex items-center gap-2">
        <Select<ActivityFilter>
          aria-label="Filter activity"
          value={selectedFilter}
          options={activityFilterOptions}
          onChange={(option) => setSelectedFilter(option.value)}
        />
      </div>

      {startsBeforeTheLogExisted && (
        <div className="text-sm text-gray-500">
          This {noun.toLowerCase()} started before activity was recorded.
          History starts on{" "}
          {moment(ACTIVITY_HISTORY_STARTS_ON).format("MMM D, YYYY")}.
        </div>
      )}

      {/* The log scrolls inside its own box with a stuck header, the same way the
          Progress tab's participant table does. */}
      <TableContainer scroll className="overscroll-x-contain max-h-[75dvh]">
        <Table className="w-full" sticky={TableSticky.onlyTop}>
          <TableHeadRow>
            <TableHeadCell roundedLeft widthPx={280}>
              Activity
            </TableHeadCell>
            <TableHeadCell widthPx={200}>Who</TableHeadCell>
            <TableHeadCell widthPx={200}>When</TableHeadCell>
            <TableHeadCell widthPx={140}>Through</TableHeadCell>
            <TableHeadCell>Details</TableHeadCell>
          </TableHeadRow>
          <TableBody>
            {rows.map((row) => (
              <ComplianceProgramActivityRow
                key={row.id}
                complianceProgramId={complianceProgramId}
                row={row}
                eventLabels={eventLabels}
                eventDetails={eventDetails}
              />
            ))}
            {rows.length === 0 && loading && (
              <TableBodyRow>
                <TableBodyCell colSpan={5} className="text-center p-4">
                  <Loading size="5" />
                </TableBodyCell>
              </TableBodyRow>
            )}
            {/* A failed request must not read as "nothing happened". This is a log:
                saying it is empty when we could not ask is the wrong lie to tell. */}
            {rows.length === 0 && !loading && error && (
              <TableBodyRow>
                <TableBodyCell
                  colSpan={5}
                  className="text-center p-4 text-gray-500"
                >
                  This activity could not be loaded. Reload the page to try
                  again.
                </TableBodyCell>
              </TableBodyRow>
            )}
            {rows.length === 0 && !loading && !error && (
              <TableBodyRow>
                <TableBodyCell
                  colSpan={5}
                  className="text-center p-4 text-gray-500"
                >
                  No activity yet.
                </TableBodyCell>
              </TableBodyRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pageInfo?.hasNextPage && (
        <div>
          <Button
            theme={buttonTheme.lightBlue}
            text="Load more"
            disabled={loading}
            onClick={handleLoadMore}
          />
        </div>
      )}
    </div>
  );
};

export default ComplianceProgramActivity;
